import { useCallback, useMemo } from 'react';

import { GetCatalogProductViewQuery } from 'packages/shared/definitions/types';

import { getGroupNavigationVariant, sortGroups } from './utils';

type Variant =
  GetCatalogProductViewQuery['productDetailByCatalogMasterId']['variants'][number];

export type VariantGroup = {
  key: string;
  name: string;
  variants: Variant[];
};

export type UseVariantGroupsReturn = {
  groups: VariantGroup[];
  selectedGroup: VariantGroup | null;
  showGroups: boolean;
  getNavigationVariant: (group: VariantGroup) => Variant;
};

type Params = {
  product: GetCatalogProductViewQuery['productDetailByCatalogMasterId'];
  currentVariant: GetCatalogProductViewQuery['productDetailByCatalogMasterId']['variants'][number];
};

/** DO NOT USE DIRECTLY IN COMPONENTS - use useProductDetailContext().variantGroups instead
 * This hook is supposed to be used in ProductDetailContext only
 * since it might get heavy on performance in case of many variants
 */
export const useVariantGroups = ({
  product,
  currentVariant,
}: Params): UseVariantGroupsReturn => {
  const getGroupingKey = useCallback((variant: Variant) => {
    return variant.parameters?.package; // here you can put some logic based on "product.variantGrouppingType"
  }, []);

  const groupingAllowed =
    product.variantGrouppingType === 'Size' &&
    product.variants.every(getGroupingKey);

  const groups = useMemo(() => {
    if (!groupingAllowed) {
      return [];
    }

    return sortGroups(
      product.variants.reduce((result, variant) => {
        const key = getGroupingKey(variant);

        if (result.some((r) => r.key === key)) {
          return result.map((r) =>
            r.key === key ? { ...r, variants: [...r.variants, variant] } : r
          );
        }

        return [
          ...result,
          {
            key,
            name: `${key} ${variant.parameters.unit}`,
            variants: [variant],
          },
        ];
      }, [] as VariantGroup[])
    );
  }, [product, getGroupingKey, groupingAllowed]);

  const selectedGroup = useMemo(
    () => groups.find((g) => g.key === getGroupingKey(currentVariant)),
    [groups, currentVariant, getGroupingKey]
  );

  return useMemo(
    () => ({
      groups,
      selectedGroup,
      showGroups: groups.length > 1,
      getNavigationVariant: (group: VariantGroup) =>
        getGroupNavigationVariant(group, currentVariant, getGroupingKey),
    }),
    [currentVariant, groups, selectedGroup, getGroupingKey]
  );
};
